/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import type { ColumnDef, RowData } from '@tanstack/react-table';
import type { TableFeatureSet } from '../Table.features';

// Function version for consistency with Selector and backward compatibility
const Expander = <T extends RowData>(): ColumnDef<TableFeatureSet, T, unknown> => ({
  id: 'expander',
  size: 40,
  minSize: 40,
  maxSize: 40,
  enableSorting: false,
  enableResizing: false,
  enableHiding: false,
  meta: {
    isVisible: true,
    className: 'ui__expander-column',
  },
  header: ({ table }) => {
    const isAllRowsExpanded = table.getIsAllRowsExpanded();

    const handleToggleAll = () => {
      table.toggleAllRowsExpanded();
    };

    return (
      <div className="ui__expander w-100 h-100 tc" onClick={handleToggleAll} style={{ cursor: 'pointer' }}>
        <span aria-expanded={isAllRowsExpanded} />
      </div>
    );
  },
  cell: ({ row, table }) => {
    const canExpand = row.getCanExpand();
    const isExpanded = row.getIsExpanded();

    if (!canExpand) {
      return null;
    }

    const handleToggle = () => {
      const { rows } = table.getRowModel();

      // Find currently expanded row
      const expandedRow = rows.find((r) => r.getIsExpanded());

      if (expandedRow && expandedRow.id !== row.id) {
        // Check if this is a sub-item of an expanded row
        const isSubItemOfRow = Boolean(expandedRow && row.id.split('.')[0] === expandedRow.id);

        if (isSubItemOfRow) {
          // Handle sub-rows expansion logic
          const expandedSubItem = expandedRow.subRows?.find((subRow) => subRow.getIsExpanded?.());

          if (expandedSubItem && expandedSubItem.id !== row.id) {
            // Collapse the previously expanded sub-item
            expandedSubItem.toggleExpanded?.(false);
          }
        } else {
          // Collapse the previously expanded row if it's not the current one
          expandedRow.toggleExpanded(false);
        }
      }

      // Toggle current row
      row.toggleExpanded();
    };

    return (
      <div className="ui__expander w-100 h-100 tc" onClick={handleToggle} style={{ cursor: 'pointer' }}>
        <span aria-expanded={isExpanded} />
      </div>
    );
  },
});

// Also export as object for backward compatibility
export const ExpanderColumn = Expander();

export default Expander;
